// Экран 1 — «Сегодня». Один вопрос: что делать сейчас.
// Набор небольшой и явно ограничен по времени, одна кнопка «Начать», рядом
// короткий режим на 8 минут. Ни серий подряд, ни значков, ни прогноза балла:
// прогноз живёт только на недельном экране.
//
// **Кнопки стоят первыми с 20 сентября 2026.** До этого до них надо было
// прочитать шапку, две строки состояния, совет дня и три полосы — то есть
// экран отвечал на «как у меня дела» раньше, чем на «что делать сейчас».
// Порядок теперь такой: кто ты и какой сегодня день → что нажать → как идёт.
//
// **Три полосы заменены тремя числами** (`.strip`). Полосы занимали треть
// первого экрана, ни одна не вела к действию, и подписи под ними повторяли
// друг друга. Осталось по одному числу на вопрос — сегодня, три недели, вся
// карта, — микрополоса вместо шкалы и ссылка «Прогресс» за подробностями.
// Единственное, что полосы говорили и что меняет решение прямо сейчас, —
// «сколько ещё сидеть»; это ушло словами в строку состояния дня,
// src/lib/day-line.ts.
//
// **Шапка персональная с 14 сентября 2026.** Обращение по имени, когда ученик
// был в прошлый раз и чем кончится нынешний темп. Прогноза БАЛЛА в ней нет и
// не будет: решение 15 запрещает цель по результату на дневном экране, а темп
// к цели — это цель освоения, и ей там и место. Разбор — в docs/12-home.md,
// счёт — в src/lib/greeting.ts.
//
// **Состав набора уехал отсюда 14 сентября 2026.** Три карточки по типам,
// сводка тем и «ход набора» описывали день, но не вели в него: блок не
// нажимается, потому что соответствия «блок → задача» нет и не будет. Здесь
// остаётся то, что отвечает на «что делать сейчас». Сам состав с 20 сентября —
// свёрнутая секция на «Прогрессе», src/ui/SetCard.tsx: своя страница и кнопка
// «Что в наборе» в ряду с «Начать» уводили с главного экрана в описание.
//
// **Закрытый набор — не конец занятия.** Число минут говорит, сколько система
// на сегодня посчитала нужным, и это оценка, а не норма и не запрет: кто хочет
// заниматься два часа, берёт добавку порциями и открывает столько навыков,
// сколько решил. Единственное, что экран делает взамен, — показывает цену
// честно: открытый навык вернётся повторениями, и счётчик открытого за день
// стоит там же, где кнопка.

import { useEffect, useRef, useState } from "react";
import {
  api,
  useMutation,
  type DayPlan,
  type OfferedSkill,
  type ProgressSnapshot,
} from "../api";
import { pickAdvice } from "../lib/advice-lines";
import { dayLine } from "../lib/day-line";
import { formatDate, plural } from "../lib/dates";
import { reasonOf } from "../lib/errors";
import { greet, paceLine, visitLine } from "../lib/greeting";
import { Neighbours } from "../ui/Neighbours";
import { IconChevron, IconCompass } from "../ui/Icons";
import { PracticeCard } from "../ui/PracticeCard";
import { MapCard, WeekCard } from "../ui/WeekCard";
import { breakDays, markReturnSeen, returnSeen } from "../lib/coach-state";
import { Return } from "./Return";
import { CoachNote } from "../ui/CoachNote";
import { SkillPicker } from "../ui/SkillPicker";

const TOPIC_KIND = { alg: "Алгебра", geo: "Геометрия", word: "Задачи", adv: "Повышенный" } as const;
const SHORT_MONTHS = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
/** «8 ноя». */
function shortDate(date: string): string {
  const [, m, d] = date.split("-").map(Number);
  return `${d} ${SHORT_MONTHS[m! - 1]}`;
}

/** Одно предложение системы для добавки после закрытого набора. */
function SkillOffer({
  offers,
  action,
  onTake,
  disabled = false,
}: {
  offers: OfferedSkill[];
  action: string;
  onTake: (skillId: string) => void;
  disabled?: boolean;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const skill = offers.find((offer) => offer.skillId === picked) ?? offers[0];
  if (skill === undefined) return null;

  return (
    <div style={{ marginTop: 12 }}>
      {editing ? (
        <div className="pick-edit">
          <SkillPicker
            items={offers.map((offer) => ({
              id: offer.skillId,
              name: offer.name,
            }))}
            value={skill.skillId}
            autoFocus
            onPick={(id) => {
              if (id !== null) setPicked(id);
              setEditing(false);
            }}
          />
        </div>
      ) : (
        <div className="pick">
          <span className="pick__name">{skill.name}</span>
          {offers.length > 1 ? (
            <button
              className="btn btn--icon"
              disabled={disabled}
              onClick={() => setEditing(true)}
            >
              Заменить
            </button>
          ) : null}
        </div>
      )}
      <div className="faint" style={{ marginTop: 8, lineHeight: 1.5 }}>
        {skill.summary}
      </div>
      <div className="row row--actions" style={{ marginTop: 12 }}>
        <button
          className="btn btn--primary"
          disabled={disabled}
          onClick={() => onTake(skill.skillId)}
        >
          {action}
        </button>
        {editing ? (
          <button className="btn btn--quiet" onClick={() => setEditing(false)}>
            Оставить как было
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function Today({
  learnerKey,
  plan,
  onStart,
  onOpenDay,
  onOpenProgress,
  onOpenTheory,
  onOpenAtlas,
  onDrill,
  onOpenPlan,
}: {
  learnerKey: string;
  plan: DayPlan | null | undefined;
  onStart: (
    itemKey: string,
    addedSkill?: { skillId: string; name: string } | null,
  ) => void;
  onOpenDay: () => void;
  /** Подробности по неделе и карте — и состав сегодняшнего набора. */
  onOpenProgress: () => void;
  onOpenTheory: (skillId: string, open: boolean) => void;
  /** Read-only атлас, сразу на выбранном навыке. */
  onOpenAtlas: (skillId: string) => void;
  /** Тренажёр по теме из карточки «Своя тема». Идёт и по закрытой. */
  onDrill: (skillId: string) => void;
  /** План по месяцам — из карточки «Тема недели». */
  onOpenPlan: () => void;
}) {
  const ensurePlan = useMutation(api.today.ensurePlan);
  const start = useMutation(api.today.start);
  const choose = useMutation(api.today.choose);
  const replaceChosen = useMutation(api.today.replaceChosen);
  const more = useMutation(api.today.more);
  const takeSnapshot = useMutation(api.progress.snapshot);
  const asked = useRef(false);

  // Недельные и целевые числа: снимок на открытии, не подписка.
  //
  // Ref, а не пустой список зависимостей: StrictMode прогоняет эффект дважды,
  // и снимок собирался бы два раза на каждое открытие. Тот же приём, что на
  // экране «Прогресс», и по той же причине.
  //
  // Ошибка снимка экран не ломает: две ячейки полосы просто не появятся. Набор
  // дня к этому запросу не привязан, и падать всему экрану из-за недельного
  // числа нельзя — задача важнее справки.
  const [snapshot, setSnapshot] = useState<ProgressSnapshot | null>(null);
  const snapshotAsked = useRef(false);
  useEffect(() => {
    if (snapshotAsked.current) return;
    snapshotAsked.current = true;
    void takeSnapshot({ learnerKey })
      .then(setSnapshot)
      .catch((e: unknown) => console.error(e));
  }, [learnerKey, takeSnapshot]);
  // «Задач больше нет» — не ошибка и не пустое нажатие: пул по открытым
  // навыкам конечен, и это надо сказать словами, а не молча ничего не сделать.
  const [drained, setDrained] = useState(false);
  // Строка дня. Ленивое начальное значение, а не вызов в разметке: иначе она
  // менялась бы на каждой перерисовке — то есть от нажатия на любую кнопку, —
  // и читалась бы как шум, а не как совет. Новая приходит с перезагрузкой
  // страницы, решение от 14 сентября 2026.
  const [advice] = useState(pickAdvice);
  // Какой из открытых навыков сейчас меняют. Идентификатор, а не флаг: за день
  // можно открыть сколько угодно навыков, и каждый меняется отдельно.
  const [swapping, setSwapping] = useState<string | null>(null);
  // Вход в задачу проверяет границу на сервере в момент нажатия. Снимок
  // подписки мог устареть во второй вкладке, а вставка первого навыка меняет
  // порядок незакрытого хвоста, поэтому `left[0]` клиент больше не выбирает.
  const [starting, setStarting] = useState(false);
  const [choosingStartSkill, setChoosingStartSkill] = useState(false);
  const [pickedStartSkillId, setPickedStartSkillId] = useState<string | null>(
    null,
  );
  const [startError, setStartError] = useState<string | null>(null);
  // Экран «Возвращение» закрыт в этой вкладке (src/screens/Return.tsx).
  const [returnClosed, setReturnClosed] = useState(false);

  const begin = (skillId?: string) => {
    if (starting) return;
    setStarting(true);
    setStartError(null);
    void start({
      learnerKey,
      date: plan?.date ?? "",
      ...(skillId ? { skillId } : {}),
    })
      .then((result) => {
        // Защита от устаревшей подписки: если сервер уже видит несколько
        // предложений, открываем отдельный экран выбора вместо скрытого изменения
        // ниже первого экрана.
        if (result.kind === "choose") {
          setChoosingStartSkill(true);
          return;
        }
        if (result.kind === "ready") onStart(result.itemKey, result.addedSkill);
      })
      .catch((error: unknown) => setStartError(reasonOf(error)))
      .finally(() => setStarting(false));
  };

  /** Взять порцию и уйти прямо в первую задачу. */
  const takeMore = () => {
    setDrained(false);
    void more({ learnerKey }).then((r) => {
      if (r.firstItemKey === null) setDrained(true);
      else onStart(r.firstItemKey);
    });
  };

  // Набор дня создаётся мутацией, а не запросом: пересчёт на каждом открытии
  // экрана менял бы набор под учеником по ходу дня.
  useEffect(() => {
    if (plan === null && !asked.current) {
      asked.current = true;
      void ensurePlan({ learnerKey, mode: "full" });
    }
  }, [plan, learnerKey, ensurePlan]);

  if (plan === undefined || plan === null) {
    return (
      <div className="stack">
        <div className="skeleton" style={{ width: 180 }} />
        <div className="skeleton" style={{ width: 320, height: 34 }} />
        <div className="card">
          <div className="skeleton" />
        </div>
      </div>
    );
  }

  const left = plan.assignments.filter((a) => !a.done);
  const doneCount = plan.assignments.length - left.length;
  // Минуты и дни до экзамена считает сервер: своя оценка на экране
  // разъезжается с той, по которой планировщик собирал набор. Складывать
  // набор и добавку экран вправе — это сложение двух серверных чисел, а не
  // вторая таблица оценок.
  const minutes = plan.minutes + plan.extraMinutes;
  const days = plan.daysToExam;
  // Темы с написанным разбором. Снимок приходит вторым вызовом, и до него
  // кнопок «Разобраться» нет — как и двух ячеек полосы: они не блокируют ни
  // одного действия, без которого день не идёт.
  const withTheory = new Set(
    (snapshot?.catalog ?? []).filter((c) => c.theory).map((c) => c.skillId),
  );
  // Две строки из снимка, обе могут молчать: прошлого визита может не быть
  // (первый день), а темп на первой неделе не судится вовсе.
  const visit = snapshot === null ? null : visitLine(snapshot.days, plan.date);
  const pace = snapshot === null ? null : paceLine(snapshot);
  // Открытые за день навыки приходят отдельным полем: `offered` после выбора
  // пересчитывается и выбранного навыка в нём уже нет — искать его там значило
  // бы показывать «пока не выбран» сразу после выбора.
  const opened = plan.opened;
  const short = plan.mode === "short";
  // День по одной теме: набор целиком из неё, повторений по другим навыкам и
  // нового навыка в нём нет. Признак — тема, а не режим: `plan.topic` не null
  // ровно при `mode: "topic"`, и проверять одно вместо двух надёжнее.
  const topicDay = plan.topic;
  // «Сколько ещё сидеть» — то единственное из трёх полос, что меняет решение
  // прямо сейчас. Считается из серверных порогов, слова — в src/lib/day-line.ts.
  const state = dayLine({
    spentMinutes: plan.meter.spentMinutes,
    goalMinutes: plan.meter.goalMinutes,
    minMinutes: plan.meter.minMinutes,
    // Невзятый предложенный навык — тоже несделанное: без него строка писала
    // «набор пройден» ученику, которому ещё только предстоит начать.
    tasksLeft: left.length + (plan.chosenSkillId === null && plan.offered.length > 0 ? 1 : 0),
  });
  // Первый навык границы — рекомендация системы. Пока новый навык на сегодня
  // не выбран, главная кнопка принимает её без промежуточного экрана.
  const recommended =
    plan.chosenSkillId === null ? (plan.offered[0] ?? null) : null;
  // Начать можно, если в наборе есть незакрытые задачи ИЛИ предложен новый
  // навык, которого ещё не брали: `today.start` добавит его сам. До 30 октября
  // 2026 экран смотрел только на задачи, и у ученика, которому повторять
  // нечего (на диагностике всё «не знаю»), набор считался закрытым — кнопки
  // «Начать» не было, хотя система предлагала навык. Нашлось пробой на dev.
  const startable = left.length > 0 || recommended !== null;
  // Подписка может пересчитать границу, пока открыт список. Если выбранный навык
  // исчез, остаёмся на экране и безопасно возвращаемся к новой рекомендации.
  //
  // Последний запасной — первый из списка, даже когда навык дня выбран: если
  // сервер ответил «выбери», экран выбора обязан открыться. Без этого ответ
  // `choose` при `recommended === null` не менял на экране ничего, и кнопка
  // «Продолжить» выглядела мёртвой (25 сентября 2026).
  const pickedStartSkill =
    plan.offered.find((skill) => skill.skillId === pickedStartSkillId) ??
    recommended ??
    plan.offered[0] ??
    null;

  if (choosingStartSkill && pickedStartSkill !== null) {
    return (
      <div className="col" style={{ maxWidth: 720, margin: "0 auto" }}>
        <div>
          <button
            type="button"
            className="link link--quiet"
            disabled={starting}
            onClick={() => {
              setChoosingStartSkill(false);
              setStartError(null);
            }}
          >
            ← Сегодня
          </button>
        </div>

        <div style={{ marginTop: 28 }}>
          <div className="eyebrow">Новый навык на сегодня</div>
          <h1 className="h1">
            Выберите один из {plan.offered.length}{" "}
            {plural(plan.offered.length, "навыка", "навыков", "навыков")}
          </h1>
          <p className="lede">
            Первый вариант рекомендует система. Можно выбрать другой — набор
            начнётся с него.
          </p>
        </div>

        <div className="choices" role="radiogroup" aria-label="Доступные навыки">
          {plan.offered.map((skill, index) => {
            const selected = skill.skillId === pickedStartSkill.skillId;
            return (
              <button
                key={skill.skillId}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`choice${selected ? " choice--on" : ""}`}
                disabled={starting}
                onClick={() => setPickedStartSkillId(skill.skillId)}
              >
                <span className="choice__mark" aria-hidden="true" />
                <span className="choice__text">
                  <span
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      flexWrap: "wrap",
                      gap: "6px 10px",
                    }}
                  >
                    <strong>{skill.name}</strong>
                    {index === 0 ? <span className="tag">Рекомендация</span> : null}
                  </span>
                  <span
                    className="faint"
                    style={{ display: "block", marginTop: 4, lineHeight: 1.45 }}
                  >
                    {skill.summary}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {startError === null ? null : (
          <div className="notice" role="alert" style={{ marginTop: 14 }}>
            {startError}
          </div>
        )}

        <div className="row row--actions" style={{ marginTop: 20 }}>
          <button
            type="button"
            className="btn btn--primary btn--big"
            disabled={starting}
            onClick={() => begin(pickedStartSkill.skillId)}
          >
            Начать с этим навыком
          </button>
          <button
            type="button"
            className="btn btn--quiet"
            disabled={starting}
            onClick={() => {
              setChoosingStartSkill(false);
              setStartError(null);
            }}
          >
            Отмена
          </button>
        </div>
      </div>
    );
  }

  // Из чего состоит остаток набора — строка под заголовком главной карточки.
  // Типы назначений те же, что у карточки набора (src/ui/SetCard.tsx).
  const leftOf = (kind: "review" | "new" | "challenge") =>
    left.filter((a) => a.kind === kind).length;
  const mix = [
    { key: "review", n: leftOf("review"), tone: "review", text: (n: number) => `${n} ${plural(n, "повторение", "повторения", "повторений")}` },
    { key: "new", n: leftOf("new"), tone: "new", text: (n: number) => `${n} — новый навык` },
    { key: "challenge", n: leftOf("challenge"), tone: "challenge", text: (n: number) => `${n} ${plural(n, "вызов", "вызова", "вызовов")} — формат экзамена` },
  ].filter((m) => m.n > 0);

  // После перерыва от четырёх дней — экран «Возвращение» вместо обычного дня
  // (макет 28 октября 2026). Только до первой задачи и один раз за день: он
  // встречает, а не стоит поперёк каждого входа.
  const gap = snapshot === null ? null : breakDays(snapshot.days, plan.date, snapshot.learnerDays);
  if (snapshot !== null && gap !== null && doneCount === 0 && !returnClosed && !returnSeen(plan.date)) {
    const close = () => {
      markReturnSeen(plan.date);
      setReturnClosed(true);
    };
    return (
      <Return
        learnerKey={learnerKey}
        plan={plan}
        snapshot={snapshot}
        gap={gap}
        starting={starting}
        onSkip={close}
        onShort={() => {
          close();
          // День по теме ученик выбрал сам — его не подменяем, просто начинаем.
          if (topicDay !== null) {
            begin();
            return;
          }
          // Короткий режим пересобирает набор только из повторений — затем
          // сразу первая задача, без второго нажатия на «Сегодня». Сбой не
          // теряется: экран уже обычный, ошибка встаёт под его кнопками.
          void ensurePlan({ learnerKey, mode: "short" })
            .then(() => begin())
            .catch((e: unknown) => setStartError(reasonOf(e)));
        }}
      />
    );
  }

  return (
    <div className="today">
      {/*
        Раскладка по макету редизайна (design/redesign.mockup.html, 28 октября
        2026): слева приветствие и одна главная карточка со всем, что нужно
        для начала, справа неделя и вся карта. Три числа полосы «Сегодня /
        Три недели / Вся карта» разошлись: сегодняшнее стало заголовком главной
        карточки, недельное и целевое — карточками справа.
      */}
      <div className="cols">
        <div className="col">
          <div>
            <div className="eyebrow">
              {plan.examTitle} · осталось {days} {plural(days, "день", "дня", "дней")}
            </div>
            <h1 className="h1">{greet(plan.name, new Date().getHours())}</h1>

            {/*
              Одна строка о дне вместо трёх. До 20 сентября 2026 здесь стояли
              «был вчера», «при нынешнем темпе…» и дата с числами — три абзаца,
              между которыми ученик искал кнопку. Визит и темп уехали в полосу
              состояния, где они подписи к числам, а не самостоятельные новости.
            */}
            <p className="lede">
              {formatDate(plan.date)} ·{" "}
              <span className="num">{plan.assignments.length}</span>{" "}
              {plural(plan.assignments.length, "задача", "задачи", "задач")} ·{" "}
              <span className="num">{minutes}</span>{" "}
              {plural(minutes, "минута", "минуты", "минут")} — оценка, не норма
              {plan.extraMinutes > 0 ? (
                <>
                  {" "}
                  · добавка <span className="num">{plan.extraMinutes}</span>
                </>
              ) : null}
            </p>
          </div>

          <section className="today-hero" aria-labelledby="today-hero-h">
            <span className="stamp" aria-hidden="true">
              {short ? "Короткий" : topicDay !== null ? "По теме" : "На сегодня"}
            </span>
            <div className="eyebrow today-hero__kicker">
              <span className="today-hero__live" aria-hidden="true" />
              Следующий шаг
            </div>
            <h2 id="today-hero-h" className="today-hero__title">
              {!startable
                ? "Набор закрыт"
                : left.length === 0
                  ? `Новый навык · ≈ ${minutes} мин`
                  : doneCount === 0
                    ? `${left.length} ${plural(left.length, "задача", "задачи", "задач")} · ≈ ${minutes} мин`
                    : `Осталось ${left.length} из ${plan.assignments.length}`}
            </h2>
            {mix.length === 0 ? null : (
              <ul className="today-mix">
                {mix.map((m) => (
                  <li key={m.key} className={`today-mix__${m.tone}`}>
                    {m.text(m.n)}
                  </li>
                ))}
              </ul>
            )}
          {recommended === null || !startable ? null : (
            <div
              style={{
                marginTop: 24,
                padding: "14px 0",
                borderTop: "1px solid var(--line)",
                borderBottom: "1px solid var(--line)",
              }}
            >
              <div className="card__label">Предложенный системой навык</div>
              <div className="pick__name" style={{ marginTop: 7 }}>
                {recommended.name}
              </div>
              <div className="faint" style={{ marginTop: 6, lineHeight: 1.5 }}>
                {recommended.summary}
              </div>
            </div>
          )}

          {/*
            Главная кнопка принимает рекомендацию системы. Полный список вынесен
            на отдельный экран: на главной больше нет второй карточки с ещё одним
            обязательным действием ниже первого экрана.
          */}
          <div className="row row--actions" style={{ marginTop: 22 }}>
            {startable ? (
              <button
                className="btn btn--primary btn--big"
                disabled={starting}
                onClick={() => begin(recommended?.skillId)}
              >
                {doneCount > 0 ? "Продолжить" : "Начать"}
              </button>
            ) : (
              <>
                <button className="btn btn--primary btn--big" onClick={takeMore}>
                  Ещё задачи
                </button>
                <button className="btn btn--big" onClick={onOpenDay}>
                  Итог дня
                </button>
              </>
            )}

            {recommended !== null && plan.offered.length > 1 && startable ? (
              <button
                type="button"
                className="btn"
                disabled={starting}
                onClick={() => {
                  setPickedStartSkillId(recommended.skillId);
                  setStartError(null);
                  setChoosingStartSkill(true);
                }}
              >
                Изменить навык и {doneCount > 0 ? "продолжить" : "начать"}
              </button>
            ) : null}

            {/*
              Смена режима дня — отдельное действие того же ряда. Пересобрать
              набор можно, только пока не закрыта ни одна задача, поэтому после
              первого ответа кнопка уходит.
            */}
            {doneCount > 0 ? null : topicDay !== null || short ? (
              <button
                className="link today-hero__alt"
                onClick={() => void ensurePlan({ learnerKey, mode: "full" })}
              >
                Полный набор
              </button>
            ) : (
              <button
                className="link today-hero__alt"
                title="Короткий режим: только повторение, около восьми минут"
                onClick={() => void ensurePlan({ learnerKey, mode: "short" })}
              >
                Только повторение — 8 мин
              </button>
            )}
          </div>

          {/*
            «Сколько ещё сидеть» — сразу под кнопкой, которой это решение и
            исполняется. До 28 сентября 2026 строка стояла заголовком над советом
            дня, и два сообщения разного рода — состояние и совет, как работать —
            читались одним блоком.
          */}
          <p className="dayline">{state}</p>

          {recommended !== null && plan.offered.length > 1 && startable ? (
            <div style={{ marginTop: 10, fontSize: 14, fontWeight: 600 }}>
              Ещё {plan.offered.length - 1}{" "}
              {plural(plan.offered.length - 1, "навык", "навыка", "навыков")}{" "}
              {plan.offered.length === 2 ? "доступен" : "доступны"}
            </div>
          ) : null}

          {startError === null ? null : (
            <div className="notice" role="alert" style={{ marginTop: 12 }}>
              {startError}
            </div>
          )}

          </section>

          {/*
            Тема недели — первые блоки плана по месяцам (src/screens/Plan.tsx).
            Папками с ярлыком, как карточки уроков teen-english. Нажатие ведёт
            на карту навыков к первому навыку темы: страницы темы целиком пока
            нет, а карта показывает и навыки, и разборы.
          */}
          {snapshot === null || snapshot.planWeek.length === 0 ? null : (
            <section className="card week-topics" aria-labelledby="wt-h">
              <div className="wk-head">
                <h3 id="wt-h">Тема недели</h3>
                <button type="button" className="btn btn--icon" onClick={onOpenPlan}>
                  Весь план <IconChevron size={14} />
                </button>
              </div>
              <div className="week-topics__list">
                {snapshot.planWeek.map((t, i) => (
                  <button
                    key={t.topicId}
                    type="button"
                    className={`topic-folder topic-folder--${i + 1}`}
                    onClick={() => onOpenAtlas(t.firstSkillId)}
                  >
                    <span className={`plan-tag plan-tag--${t.kind}`}>
                      {TOPIC_KIND[t.kind]} · {t.ref}
                    </span>
                    <b>{t.title}</b>
                    <span className="topic-folder__bar" aria-hidden="true">
                      <i style={{ width: `${(t.topicDone / Math.max(1, t.topicTotal)) * 100}%` }} />
                    </span>
                    <span className="topic-folder__meta num">
                      {t.topicDone} из {t.topicTotal}{" "}
                      {plural(t.topicTotal, "навыка", "навыков", "навыков")} · до {shortDate(t.to)}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {opened.length > 0 ? (
            <div className="card card--act">
              <div className="card__label card__label--act">
                {opened.length > 1
                  ? `Открыто сегодня · ${opened.length}`
                  : "Новый навык на сегодня"}
              </div>
              <div className="stack" style={{ marginTop: 12 }}>
                {opened.map((skill, i) => (
                  <div key={skill.skillId}>
                    {i > 0 ? (
                      <hr className="hr" style={{ margin: "0 0 14px" }} />
                    ) : null}
                    {/*
                      Замена выбранного навыка. До 20 сентября 2026 выбор был
                      необратим: сервер умел только добавлять, и ученик, нажавший
                      не туда, жил с чужим навыком до завтра. Граница — первая
                      ПОПЫТКА по навыку: она уже легла в журнал, который не
                      чистится никогда. Считает её сервер, `canReplace`.
                    */}
                    {swapping === skill.skillId ? (
                      <div className="pick-edit">
                        <SkillPicker
                          items={[
                            { id: skill.skillId, name: skill.name },
                            ...plan.offered.map((o) => ({
                              id: o.skillId,
                              name: o.name,
                            })),
                          ]}
                          value={skill.skillId}
                          autoFocus
                          onPick={(id) => {
                            setSwapping(null);
                            if (id === null || id === skill.skillId) return;
                            void replaceChosen({
                              learnerKey,
                              fromSkillId: skill.skillId,
                              toSkillId: id,
                            });
                          }}
                        />
                      </div>
                    ) : (
                      <div className="pick">
                        <div className="pick__title">
                          <span className="pick__name">{skill.name}</span>
                          <button
                            className="btn btn--icon pick__atlas"
                            type="button"
                            title="Показать навык на карте курса"
                            aria-label={`Показать навык «${skill.name}» на карте курса`}
                            onClick={() => onOpenAtlas(skill.skillId)}
                          >
                            <IconCompass size={18} />
                          </button>
                        </div>
                        {skill.canReplace ? (
                          <button
                            className="btn btn--icon"
                            onClick={() => setSwapping(skill.skillId)}
                          >
                            Заменить
                          </button>
                        ) : null}
                      </div>
                    )}
                    <div
                      className="faint"
                      style={{ marginTop: 8, lineHeight: 1.5 }}
                    >
                      {skill.summary}
                    </div>
                    {/*
                      Окрестность навыка: на чём стоит и что откроет. Карты
                      целиком у ученика нет, и это не её урезанный вид, а подпись
                      к одному навыку — разбор в docs/02-decisions.md.
                    */}
                    <Neighbours skill={skill} />
                    {/*
                      «Разобраться» стоит только там, где разбор написан: пилот
                      покрывает не всю карту, и кнопка, ведущая в «разбора по
                      этой теме пока нет», — это обещание, которого система не
                      давала.
                    */}
                    {withTheory.has(skill.skillId) ? (
                      <button
                        className="btn btn--quiet"
                        style={{
                          marginTop: 10,
                          padding: "8px 16px",
                          fontSize: 14,
                        }}
                        // Навык из этого списка открыт по построению: в набор
                        // он попал сегодня выбором ученика.
                        onClick={() => onOpenTheory(skill.skillId, true)}
                      >
                        Разобраться
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
              {/* Условие замены говорится один раз на карточку и только пока оно
                  правда: строка «заменить уже нельзя» под навыком, по которому
                  задача решена, — сожаление о несделанном, а не помощь. */}
              {opened.some((s) => s.canReplace) ? (
                <div className="faint" style={{ marginTop: 12, fontSize: 12 }}>
                  Заменить можно, пока не решена первая задача по навыку
                </div>
              ) : null}
              {opened.length > 1 ? (
                <div
                  className="muted"
                  style={{ marginTop: 10, fontSize: 14, lineHeight: 1.5 }}
                >
                  Каждый из них вернётся повторениями в следующие дни — набор
                  станет плотнее, и это нормально, пока успеваешь.
                </div>
              ) : null}
            </div>
          ) : null}


          {!startable ? (
            <div className="card card--dashed">
              <div className="card__label">Дальше</div>
              <div
                className="muted"
                style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}
              >
                Дневной нормы, которая запирает, здесь нет: минуты набора — оценка
                времени, а не запрет. Порция добавки —{" "}
                {topicDay === null ? (
                  <>
                    около <span className="num">10</span> минут по уже открытым
                    навыкам
                  </>
                ) : (
                  // В день по теме добавка берёт ту же тему, а не долги по
                  // расписанию: иначе кнопка «Ещё задачи» приводила бы в набор
                  // «по одной теме» ровно то, от чего режим ученика избавил.
                  <>
                    ещё <span className="num">3</span> задачи по теме «
                    {topicDay.name}»
                  </>
                )}
                , и нажимать можно сколько угодно раз.
              </div>
              {drained ? (
                <div
                  className="muted"
                  style={{ marginTop: 12, fontSize: 14, lineHeight: 1.6 }}
                >
                  {topicDay !== null ? (
                    <>
                      Свободные задачи по теме «{topicDay.name}» на сегодня
                      кончились. Обычный набор вернётся завтра вместе с
                      отложенными повторениями.
                    </>
                  ) : (
                    <>
                      Задачи по открытым навыкам на сегодня кончились. Дальше —
                      только новый навык
                      {plan.offered.length === 0
                        ? ", а открыть пока нечего: предпосылки следующих навыков ещё не набрали порог."
                        : " из списка ниже."}
                    </>
                  )}
                </div>
              ) : null}
              <div className="row row--actions" style={{ marginTop: 16 }}>
                <button className="btn btn--quiet" onClick={takeMore}>
                  Ещё задачи
                </button>
              </div>
              {/* Ещё один навык сверх взятого: тот же контрол, что и в карточке
                  выше, — одно предложение и «Заменить». Показывается только
                  когда навык за сегодня уже брали: до этого предложение стоит
                  своей карточкой, и две копии одного выбора читались бы как два
                  разных. */}
              {plan.offered.length > 0 && opened.length > 0 ? (
                <>
                  <hr className="hr" />
                  <div className="card__label">Открыть ещё навык</div>
                  <SkillOffer
                    offers={plan.offered}
                    action="Открыть"
                    onTake={(skillId) => void choose({ learnerKey, skillId })}
                  />
                </>
              ) : null}
            </div>
          ) : null}

          {/*
            Совет дня — о том, КАК работать, — наклейкой, как в teen-english.
            Оценки того, как ученик работает, здесь нет и не будет: похвала
            привязывает продолжение к чужому одобрению.
          */}
          {/* Меньше 60 дней до экзамена — вместо совета дня говорит тренер. */}
          {days >= 0 && days <= 60 ? (
            <CoachNote className="today-tip" situation="examNear" vars={{ left: days }} seedParts={[learnerKey, plan.date]} />
          ) : (
            <div className="sticker today-tip">
              <IconCompass size={20} />
              <p>{advice}</p>
            </div>
          )}

          {topicDay === null ? null : (
            <div className="muted" style={{ marginTop: 12, fontSize: 14 }}>
              День по теме «{topicDay.name}»: повторений по другим навыкам и
              нового навыка в нём нет.
            </div>
          )}
        </div>

        <div className="col">
          {snapshot === null ? null : (
            <>
              <WeekCard weeks={snapshot.weeks} daysPerWeek={snapshot.window.daysPerWeek} learnerKey={learnerKey} />
              <MapCard
                snapshot={snapshot}
                visit={visit}
                pace={pace}
                onOpenProgress={onOpenProgress}
              />
            </>
          )}
          {/*
            Своя тема. Снимок приходит вторым вызовом, поэтому карточка
            появляется на мгновение позже набора; своего скелета ей не надо — она
            не блокирует ни одного действия, без которого день не идёт.
          */}
          {snapshot === null ? null : (
            <PracticeCard
              learnerKey={learnerKey}
              plan={plan}
              catalog={snapshot.catalog}
              sections={snapshot.sections}
              onStart={onStart}
              onOpenTheory={onOpenTheory}
              onDrill={onDrill}
            />
          )}

        </div>
      </div>
    </div>
  );
}
